import React, { useState, useEffect, useRef } from "react"
import { bindActionCreators } from "redux"
import { connect } from "react-redux"
import Title from "../general/title"
import _ from "lodash"
import moment from "moment"
import { Line } from "react-chartjs-2"
import numeral from "numeral"
import colorArray from "../../helpers/colorArray"
import general from "helpers/general"
import ImageExport from "components/ImageExport"
import FullTable from "components/FullTable"
import helpers from "helpers/helpers"
import classnames from "classnames"

function HomeSalesTrend(props) {
	const [options, setOptions] = useState({
		plugins: {
			datalabels: { display: false },
		},
		legend: {
			display: true,
			position: "bottom",
			labels: {
				boxWidth: 20,
				usePointStyle: true,
			},
		},
	})
	const [monthlyColumns, setMonthlyColumns] = useState([
		{
			Header: "Month",
			accessor: "month",
		},
		{
			Header: `${moment().year()} Gross`,
			accessor: "currentGross",
			Cell: (v) => numeral(v.value).format("0,0"),
			Footer: (props) => helpers.tableTotals(props, "currentGross", "0,0"),
		},
		{
			Header: `${moment().year()} Net`,
			accessor: "currentNet",
			Cell: (v) => numeral(v.value).format("0,0"),
			Footer: (props) => helpers.tableTotals(props, "currentNet", "0,0"),
		},
		{
			Header: `${moment().year() - 1} Net`,
			accessor: "lastNet",
			Cell: (v) => numeral(v.value).format("0,0"),
			Footer: (props) => helpers.tableTotals(props, "lastNet", "0,0"),
		},
		{
			Header: `Percent`,
			accessor: "percent",
			Cell: (v) => numeral(v.value).format("0,0.0%"),
		},
		{
			Header: `${moment().year()} vs. ${moment().year() - 1}`,
			accessor: "versus",
			Cell: (v) => `${numeral(v.value).format("0,0.0")}%`,
		},
	])
	const [columns, setColumns] = useState([
		{
			Header: "Year",
			accessor: "year",
		},
		{
			Header: "Total",
			accessor: "total",
			Cell: (v) => numeral(v.value).format("0,0"),
		},
	])
	const [tableData, setTableData] = useState([])
	const [monthlyData, setMonthlyData] = useState([])
	const [cumulativeData, setCumulativeData] = useState({
		labels: [],
		datasets: [],
	})
	const [data, setData] = useState({
		labels: [],
		datasets: [],
	})
	const [yoyPerformance, setYoyPerformance] = useState(null)
	const [activeYears, setActiveYears] = useState([])
	const [allYears, setAllYears] = useState([])

	const prevPropsRef = useRef()

	useEffect(() => {
		build()
	}, [])

	useEffect(() => {
		if (prevPropsRef.current && !_.isEqual(props, prevPropsRef.current)) {
			build()
		}
		prevPropsRef.current = props
	}, [props])

	function build() {
		determineYears()
		buildData()
		buildCumulativeData()
		buildMonthlyData()
	}

	function determineYears() {
		let years = []
		_.forEach(props.payload.exec.sales, (v) => {
			let year = moment.unix(v.sale_date).year()
			if (!years.includes(year)) {
				years.push(year)
			}
		})
		years.sort()
		years = _.reverse(years)
		setAllYears(years)
		setActiveYears(years)
	}

	function yearAction(v) {
		let years = _.xor(activeYears, [v])
		setActiveYears(years)
	}

	useEffect(() => {
		buildData()
		buildCumulativeData()
		buildMonthlyData()
	}, [activeYears])

	function buildCumulativeData() {
		let max = 10
		let store = {}
		let f = {}
		let arrays = {}
		let ret = {}
		_.forEach(props.payload.exec.sales, (v) => {
			if (v.cancel_date || v.transfer_date) {
				return
			}
			let year = moment.unix(v.sale_date).year()
			if (year < moment().year() - max) {
				return
			}
			let month = moment.unix(v.sale_date).format("MMMM")
			let path = `${year}.${month}`
			if (!_.has(store, path)) {
				_.set(store, path, 0)
			}
			store[year][month]++
			if (v.bustout_date) {
				year = moment.unix(v.bustout_date).year()
				month = moment.unix(v.bustout_date).format("MMMM")
				path = `${year}.${month}`
				if (!_.has(store, path)) {
					_.set(store, path, 0)
				}
				store[year][month]--
			}
		})
		_.forEach(store, (v, year) => {
			_.forEach(general.monthsArray, (month, i) => {
				if (parseInt(year) == moment().year() && i > moment().month()) {
					return
				}
				if (!v.hasOwnProperty(month)) {
					_.set(f, `${year}.${month}`, 0)
				} else {
					_.set(f, `${year}.${month}`, store[year][month])
				}
			})
		})
		_.forEach(f, (v, year) => {
			arrays[year] = []
			_.forEach(v, (r) => {
				arrays[year].push(r)
			})
		})
		_.forEach(arrays, (v, year) => {
			let run = 0
			ret[year] = []
			_.forEach(v, (num) => {
				run = run + num
				ret[year].push(run)
			})
		})
		let tableData = []
		_.forEach(ret, (v, k) => {
			let sum = _.last(v)
			tableData.push({
				year: k,
				total: sum,
			})
		})
		let datasets = []
		let i = 0
		_.forEach(ret, (v, year) => {
			if (year > moment().year()) {
				return
			}
			if (!activeYears.includes(parseInt(year))) {
				i++
				return
			}
			datasets.push({
				fill: false,
				borderColor: colorArray[i],
				backgroundColor: colorArray[i],
				label: year,
				data: v,
			})
			i++
		})
		let data = {
			labels: general.monthsArray,
			datasets,
		}
		let cumulativeData = data
		let years = [moment().year(), moment().year() - 1]
		let month = moment().month()
		let percent = null
		try {
			let previous = ret[years[1]][month]
			let current = ret[years[0]][month]
			if (previous && current) percent = numeral(helpers.percentChange(previous, current)).format("0,0.0")
		} catch (e) {
			console.log(e)
		}
		setCumulativeData(cumulativeData)
		setTableData(tableData)
		setYoyPerformance(percent)
	}

	function buildData() {
		let max = 10
		let store = {}
		let f = {}
		let arrays = {}
		_.forEach(props.payload.exec.sales, (v) => {
			if (v.cancel_date || v.transfer_date) {
				return
			}
			let year = moment.unix(v.sale_date).year()
			if (year < moment().year() - max) {
				return
			}
			let month = moment.unix(v.sale_date).format("MMMM")
			let path = `${year}.${month}`
			if (!_.has(store, path)) {
				_.set(store, path, 0)
			}
			store[year][month]++
			if (v.bustout_date) {
				year = moment.unix(v.bustout_date).year()
				month = moment.unix(v.bustout_date).format("MMMM")
				path = `${year}.${month}`
				if (!_.has(store, path)) {
					_.set(store, path, 0)
				}
				store[year][month]--
			}
		})
		_.forEach(store, (v, year) => {
			_.forEach(general.monthsArray, (month, i) => {
				if (parseInt(year) == moment().year() && i > moment().month()) {
					return
				}
				if (!v.hasOwnProperty(month)) {
					_.set(f, `${year}.${month}`, 0)
				} else {
					_.set(f, `${year}.${month}`, store[year][month])
				}
			})
		})
		_.forEach(f, (v, year) => {
			arrays[year] = []
			_.forEach(v, (r) => {
				arrays[year].push(r)
			})
		})
		let datasets = []
		let i = 0
		_.forEach(arrays, (v, year) => {
			if (year > moment().year()) {
				return
			}
			if (!activeYears.includes(parseInt(year))) {
				i++
				return
			}
			datasets.push({
				fill: false,
				borderColor: colorArray[i],
				backgroundColor: colorArray[i],
				label: year,
				data: v,
			})
			i++
		})
		let data = {
			labels: general.monthsArray,
			datasets,
		}
		setData(data)
	}

	function buildMonthlyData() {
		let ret = {}
		let store = {}
		let years = [moment().year(), moment().year() - 1]
		let months = [
			"January",
			"February",
			"March",
			"April",
			"May",
			"June",
			"July",
			"August",
			"September",
			"October",
			"November",
			"December",
		]
		_.forEach(months, (v) => {
			_.forEach(years, (year) => {
				_.setWith(store, `${year}.${v}`, { gross: 0, net: 0 }, Object)
			})
		})
		_.forEach(props.payload.exec.sales, (v) => {
			let year = moment.unix(v.sale_date).year()
			if (!_.includes(years, year)) {
				return
			}
			let month = moment.unix(v.sale_date).format("MMMM")
			store[year][month].gross++
			store[year][month].net++
		})
		_.forEach(props.payload.exec.busts, (v) => {
			let year = moment.unix(v.bustout_date).year()
			if (!_.includes(years, year)) {
				return
			}
			let month = moment.unix(v.bustout_date).format("MMMM")
			store[year][month].net--
		})
		_.forEach(store[years[0]], (v, month) => {
			try {
				let percent = store[years[0]][month].net / store[years[1]][month].net
				let versus = (store[years[0]][month].net / store[years[1]][month].net) * 100 - 100
				let i = {
					currentGross: store[years[0]][month].gross,
					lastGross: store[years[1]][month].gross,
					currentNet: store[years[0]][month].net,
					lastNet: store[years[1]][month].net,
					percent,
					versus,
				}
				_.set(ret, `${month}`, i)
			} catch (e) {
				console.log(e)
			}
		})
		let f = []
		_.forEach(ret, (v, month) => {
			f.push({
				month,
				...v,
			})
		})
		setMonthlyData(f)
	}

	return props.payloadStatus ? (
		<div>
			<Title title="Net Home Sales Trend" showDate={false} />
			<div className="segmentation content">
				<div className="imageGroup">
					<div className="yearList">
						<ul className="yearList__list">
							{_.map(allYears, (v) => {
								return (
									<li
										key={v}
										className={classnames("yearList__item", {
											active: activeYears.includes(v),
										})}
										onClick={() => yearAction(v)}>
										{v}
									</li>
								)
							})}
						</ul>
					</div>
					<div className="flex justify-end">
						<div className="bg-blue px-4 py-2 text-white font-bold text-sm inline-block">
							YoY Performance: {`${yoyPerformance ? `${yoyPerformance}%` : "N/A"}`}
						</div>
					</div>
					<h3>Cumulative Home Sales Trend</h3>
					<ImageExport name="CumulativeSalesTrend" block={true} />
					<Line data={cumulativeData} options={options} />
				</div>
				<div className="imageGroup">
					<h3>Home Sales Trend by Month</h3>
					<ImageExport name="SalesTrend" block={true} />
					<Line data={data} options={options} />
				</div>
				<div className="imageGroup">
					<h2>Table Breakout</h2>
					<FullTable
						data={tableData}
						columns={columns}
						pageSize={10}
						name="homeSalesTrend"
						title="Home Sales Trend"
					/>
					<h2>Current Year Over Year Monthly Home Sales Trend</h2>
					<p className="text-sm">For all properties selected above.</p>
					<FullTable
						data={monthlyData}
						columns={monthlyColumns}
						pageSize={10}
						name="homeSalesTrend__monthly"
						title="Home Sales Trend Monthly"
					/>
				</div>
			</div>
		</div>
	) : (
		""
	)
}

function mapStateToProps(state) {
	return {
		currentDates: state.currentDates,
		compareDates: state.compareDates,
		payload: state.payload,
		payloadStatus: state.payloadStatus,
	}
}

function matchDispatchToProps(dispatch) {
	return bindActionCreators({}, dispatch)
}

export default connect(mapStateToProps, matchDispatchToProps)(HomeSalesTrend)
